/**
 * Invoice PDF filename + file-size helpers.
 *
 * Filename pattern (Create / template save / Overview download):
 *   `{Client} {Invoice|Proforma|Credit Note|Debit Note} {InvoiceId} ({Mon-YYYY}).pdf`
 * e.g. `Bajaj Invoice SH1697 (Jun-2026).pdf`
 *
 * Max upload/generated size: 2 MB.
 */

export {
  sanitizeInvoiceFilenameBase,
  formatBillingMonthForInvoiceName,
  buildAutoInvoiceName,
  resolveInvoiceDownloadBaseName,
  buildInvoiceDownloadFilename,
  buildEInvoiceDownloadFilename,
} from './invoice-download-filename'

/** 2 MB hard limit for generated invoice PDFs and invoice file uploads. */
export const INVOICE_MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024
export const INVOICE_MAX_FILE_SIZE_LABEL = '2 MB'
export const INVOICE_FILE_TOO_LARGE_MESSAGE = 'Invoice file must be 2 MB or less'

/**
 * Create supporting-doc allow-list. Frontend sends Excel/CSV; SattvaATS
 * invoiceRoutes.js multer fileFilter must also allow .xls/.xlsx/.csv or create 500s.
 */
export const INVOICE_CREATE_ALLOWED_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'txt', 'xls', 'xlsx', 'csv']
export const INVOICE_CREATE_ALLOWED_MIMES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'text/plain',
  'text/csv',
  'application/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]
export const INVOICE_CREATE_ACCEPT_ATTR = '.pdf,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.csv'
export const INVOICE_FILE_TYPE_NOT_ALLOWED_MESSAGE = 'File type not allowed. Allowed: PDF, PNG, JPG, TXT, XLS, XLSX, CSV.'
export const INVOICE_SUPPORTING_DOC_REQUIRED_MESSAGE = 'At least one supporting document is required'

/** Prod multer (pre item-3) only allows these — Excel/CSV 500s on old fileFilter. */
export const INVOICE_PROD_MULTER_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'txt']
export const INVOICE_PROD_MULTER_MIMES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'text/plain',
]

function invoiceUploadExtension(fileOrBlob) {
  const name = (fileOrBlob && (fileOrBlob.name || fileOrBlob.filename)) || ''
  const m = String(name).toLowerCase().match(/\.([a-z0-9]+)$/)
  return m ? m[1] : ''
}

/**
 * True when the file matches the backend create upload allow-list.
 * @param {{ name?: string, type?: string }|null|undefined} fileOrBlob
 */
export function isInvoiceCreateUploadAllowed(fileOrBlob) {
  if (!fileOrBlob) return false
  const ext = invoiceUploadExtension(fileOrBlob)
  if (ext && INVOICE_CREATE_ALLOWED_EXTENSIONS.includes(ext)) return true
  const mime = String(fileOrBlob.type || '').toLowerCase().split(';')[0].trim()
  if (mime && INVOICE_CREATE_ALLOWED_MIMES.includes(mime)) return true
  return false
}

/** True when the file matches the *currently deployed* prod multer allow-list. */
export function isInvoiceProdMulterAllowed(fileOrBlob) {
  if (!fileOrBlob) return false
  const ext = invoiceUploadExtension(fileOrBlob)
  if (ext && INVOICE_PROD_MULTER_EXTENSIONS.includes(ext)) return true
  const mime = String(fileOrBlob.type || '').toLowerCase().split(';')[0].trim()
  if (mime && INVOICE_PROD_MULTER_MIMES.includes(mime)) return true
  return false
}

/**
 * @param {{ name?: string, type?: string }|null|undefined} fileOrBlob
 * @returns {string|null} Error message if type is not allowed; otherwise null.
 */
export function getInvoiceFileTypeError(fileOrBlob) {
  if (!fileOrBlob) return null
  if (isInvoiceCreateUploadAllowed(fileOrBlob)) return null
  return INVOICE_FILE_TYPE_NOT_ALLOWED_MESSAGE
}

/**
 * @param {{ size?: number }|null|undefined} fileOrBlob
 * @returns {boolean}
 */
export function isInvoiceFileWithinSizeLimit(fileOrBlob) {
  if (!fileOrBlob || typeof fileOrBlob.size !== 'number') return true
  return fileOrBlob.size <= INVOICE_MAX_FILE_SIZE_BYTES
}

/**
 * @param {{ size?: number }|null|undefined} fileOrBlob
 * @returns {string|null} Error message if over limit; otherwise null.
 */
export function getInvoiceFileSizeError(fileOrBlob) {
  if (!fileOrBlob || typeof fileOrBlob.size !== 'number') return null
  if (fileOrBlob.size <= INVOICE_MAX_FILE_SIZE_BYTES) return null
  return INVOICE_FILE_TOO_LARGE_MESSAGE
}
